import type { EventSummary } from '@finder/contracts';
import { apiUrl } from '../api-client.js';
import { trackOutboundClick } from '../lib/analytics.js';
import { CategoryArt } from './CategoryArt.js';
import { DateTile } from './DateTile.js';
import { Icon } from './Icon.js';
import { OfferBadges } from './OfferBadges.js';
import { CATEGORY_LABEL, deriveCategory } from './category.js';
import { distanceLabel, readableTeaser, timingLabel } from './format.js';
import { shortPlace } from './short-place.js';

/**
 * List card (docs/UX-REDESIGN.md §4). The title is an h3 under the results
 * heading. Reading order: date tile, category, title, one sentence, short
 * place, badges, then the original-page link.
 *
 * The link stays the opaque /api/v1/out/{id} href and still records
 * outbound_click. The title opens the in-app detail and records
 * event_detail_open. The stretched hit target stops at the original-page link.
 */
export function EventCard({
  event,
  onOpenDetail,
  selected = false,
  distanceVoice = 'place',
}: {
  event: EventSummary;
  onOpenDetail?: (canonicalId: string) => void;
  selected?: boolean;
  distanceVoice?: 'gps' | 'place';
}): JSX.Element {
  const titleId = `title-${event.canonicalId}`;
  const teaser = readableTeaser(event.teaser);
  const sourceHref = apiUrl(event.primarySourceLink.href);
  const category = deriveCategory({
    title: event.title,
    teaser: event.teaser,
    sourceNames: event.sourceNames,
    primarySourceName: event.primarySourceLink.sourceName,
    audienceTags: event.audienceTags,
  });
  const place = shortPlace(event.venueName);
  const distance = distanceLabel(event.distanceMiles, distanceVoice);
  const placeLine = place ? `${place} · ${distance}` : distance;

  return (
    <article
      className={`event-card${selected ? ' event-card--selected' : ''}`}
      aria-labelledby={titleId}
    >
      <DateTile
        startInstant={event.startInstant}
        venueTimeZone={event.venueTimeZone}
        decorative={event.timingBand === 'starting_soon'}
      />
      <div className="card-main">
        <div className="card-top">
          <CategoryArt category={category} />
          <h3 id={titleId} className="event-title">
            {onOpenDetail ? (
              <button
                type="button"
                className="event-title__open"
                onClick={() => onOpenDetail(event.canonicalId)}
              >
                <span className="event-title__text">{event.title}</span>
                <span className="event-title__details">
                  Details
                  <Icon name="chevron" size={16} />
                </span>
              </button>
            ) : (
              event.title
            )}
          </h3>
        </div>
        <TimingStrip event={event} />
        {teaser ? <p className="event-teaser">{teaser}</p> : null}
        <div className="card-meta">
          <span className="pill pill--place">{placeLine}</span>
          <span className="category-name">{CATEGORY_LABEL[category]}</span>
        </div>
        <OfferBadges event={event} />
        {event.audienceTags.length > 0 ? (
          <ul className="audience" aria-label="who this is for">
            {event.audienceTags.map((tag) => (
              <li
                key={tag.code}
                className={`tag ${tag.restricted ? 'tag--restricted' : 'tag--open'}`}
                title={tag.evidence ? `From the source: “${tag.evidence}”` : undefined}
              >
                <Icon name="users" size={13} />
                {tag.label}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="card-actions">
          <a
            className="card-outbound"
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackOutboundClick({
                eventId: event.canonicalId,
                eventTitle: event.title,
                url: sourceHref,
              })
            }
          >
            Original page
            <span className="visually-hidden"> (opens in a new tab)</span>
          </a>
        </div>
      </div>
    </article>
  );
}

/** Happening-now and starting-soon stay in text. A future start is the date tile. */
function TimingStrip({ event }: { event: EventSummary }): JSX.Element | null {
  if (event.timingBand === 'ongoing') {
    return (
      <span className="timing timing--now">
        <span className="live-dot" aria-hidden="true" />
        {timingLabel(event)}
      </span>
    );
  }
  if (event.timingBand === 'starting_soon') {
    return (
      <span className="timing timing--soon">
        <Icon name="zap" size={15} />
        {timingLabel(event)}
      </span>
    );
  }
  return null;
}
